<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="!scorecard || scorecard.compiled_score == null" class="dw-empty">
      No fleet driver data available for {{ monthLabel }} yet.
    </div>
    <template v-else>
      <div class="dw-compiled-score-badge" :class="scoreClass">
        <span class="dw-score-number">{{ scorecard.compiled_score }}</span>
        <span class="dw-score-label">/ 100 fleet average &middot; {{ scoreTier }} &middot; {{ monthLabel }}</span>
      </div>
      <!-- the Fleet Overview stats row from the full Driver Manager
           dashboard (driver_manager_dashboard.vue), minus its Fleet Avg
           Combined Score card, which is the badge above. counts come from the
           same /driver-scorecard/all payload the dashboard reads, so the two
           surfaces cannot disagree. -->
      <div class="dw-stat-grid dw-stat-grid--compact" style="margin-top: 12px">
        <div class="dw-stat-tile dw-stat-tile--compact">
          <div class="dw-stat-tile-label">Total Drivers</div>
          <div class="dw-stat-tile-value">{{ totalDrivers }}</div>
        </div>
        <div v-for="tile in tierTiles" :key="tile.tier" class="dw-stat-tile dw-stat-tile--compact">
          <div class="dw-stat-tile-label">{{ tile.tier }}</div>
          <div class="dw-stat-tile-value" :class="tile.cls">{{ tile.count }} <small>{{ tile.pct }}%</small></div>
        </div>
      </div>
      <!-- the headline KPI figures, conditionally colored against the
           tenant's thresholds. same compact tile as the tier row above:
           two rows of one size read as one block, where a mixed pair read
           as two widgets stacked. -->
      <div class="dw-stat-grid dw-stat-grid--compact" style="margin-top: 8px">
        <div v-if="scorecard.risk_score != null" class="dw-stat-tile dw-stat-tile--compact">
          <div class="dw-stat-tile-label">Safety Score</div>
          <div class="dw-stat-tile-value" :class="fieldClass('risk_score', scorecard.risk_score)">{{ scorecard.risk_score }}</div>
        </div>
        <div v-if="scorecard.idling_percentage != null" class="dw-stat-tile dw-stat-tile--compact">
          <div class="dw-stat-tile-label">Idling %</div>
          <div class="dw-stat-tile-value" :class="fieldClass('idling_percentage', scorecard.idling_percentage)">{{ formatNumber(scorecard.idling_percentage) }}%</div>
        </div>
        <div v-if="scorecard.miles_per_gallon != null" class="dw-stat-tile dw-stat-tile--compact">
          <div class="dw-stat-tile-label">MPG</div>
          <div class="dw-stat-tile-value" :class="fieldClass('miles_per_gallon', scorecard.miles_per_gallon)">{{ formatNumber(scorecard.miles_per_gallon) }}</div>
        </div>
        <div v-if="scorecard.dvir_completion_rate != null" class="dw-stat-tile dw-stat-tile--compact">
          <div class="dw-stat-tile-label">DVIRs / (Bus Days × 2)</div>
          <div class="dw-stat-tile-value" :class="fieldClass('dvir_completion_rate', scorecard.dvir_completion_rate)">{{ formatNumber(scorecard.dvir_completion_rate, 1) }}%</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { cm_get_all_driver_scorecards } from '../../networking/apis'
import { get_all_driver_scorecards } from '../../networking/tbn_apis'
import { mergeThresholds, getCompiledScoreClass, getFieldClass, tierClass } from '../../utils/driver_manager_thresholds'

// the dashboard's card order, so someone glancing between the two reads the
// same left-to-right sequence.
const TIER_ORDER = ['Elite', 'Solid', 'Fair', 'High Risk', 'Insufficient Data']

export default {
  name: 'widget_driver_manager',
  props: {
    tenant: { type: String, required: true },
    isCmTenant: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: true,
      scorecard: null,
      drivers: [],
      thresholds: null,
    }
  },
  computed: {
    // The last COMPLETE month, never the current one: every scorecard
    // endpoint strips the current month from the requested range before it
    // reads anything (computing it live fleet-wide is too slow), so asking
    // for it always comes back empty.
    reportMonth() {
      return moment().subtract(1, 'month')
    },
    monthLabel() {
      return this.reportMonth.format('MMM YYYY')
    },
    mergedThresholds() {
      return mergeThresholds(this.thresholds)
    },
    // dashboard parity: Total Drivers is every driver with activity in the
    // period (drivers.length there), Insufficient Data included.
    totalDrivers() {
      return this.drivers.length
    },
    tierCounts() {
      const counts = { Elite: 0, Solid: 0, Fair: 0, 'High Risk': 0, 'Insufficient Data': 0 }
      this.drivers.forEach(d => {
        if (counts[d.compiled_score_tier] !== undefined) counts[d.compiled_score_tier]++
      })
      return counts
    },
    // a tier nobody is in is a zero taking up a tile rather than a statistic --
    // the dashboard drops Insufficient Data on the same reasoning. the four
    // scored tiers always show, so the row keeps its shape month to month.
    tierTiles() {
      return TIER_ORDER
        .filter(tier => tier !== 'Insufficient Data' || this.tierCounts[tier])
        .map(tier => ({
          tier,
          count: this.tierCounts[tier],
          pct: this.totalDrivers ? Math.round(this.tierCounts[tier] / this.totalDrivers * 100) : 0,
          cls: tierClass(tier, 'text'),
        }))
    },
    // getCompiledScoreClass returns e.g. 'compiled-score-elite', now defined
    // once in the shared family (assets/css/driver-manager-mobile.css) -- the
    // old dw- prefixed copy of the tier tints is gone.
    scoreClass() {
      const cls = getCompiledScoreClass(Number(this.scorecard.compiled_score), this.mergedThresholds.compiled_score, this.scoreTier)
      return cls || 'compiled-score-solid'
    },
    // The backend is the source of truth: it's the only place with access to
    // Insufficient Data. Only fall back to recomputing from a raw
    // elite/solid/coaching cfg if the backend didn't supply a tier at all.
    scoreTier() {
      if (this.scorecard.compiled_score_tier) return this.scorecard.compiled_score_tier
      const cfg = this.mergedThresholds.compiled_score
      const s = Number(this.scorecard.compiled_score)
      if (cfg) {
        if (s >= Number(cfg.elite)) return 'Elite'
        if (s >= Number(cfg.solid)) return 'Solid'
        if (s >= Number(cfg.coaching)) return 'Fair'
        return 'High Risk'
      }
      return ''
    },
  },
  created() {
    this.load()
  },
  methods: {
    fieldClass(fieldName, value) {
      return getFieldClass(fieldName, value, this.mergedThresholds)
    },
    formatNumber(value, precision = 2) {
      if (value === null || value === undefined) return '—'
      const n = Number(value)
      return isFinite(n) ? n.toFixed(precision) : value
    },
    async load() {
      this.loading = true
      try {
        const month = this.reportMonth.format('YYYY-MM')
        const fetch = this.isCmTenant
          ? cm_get_all_driver_scorecards(this.tenant, month, month)
          : get_all_driver_scorecards(this.tenant, month, month)
        const response = await fetch
        const responseData = (response && response.value && response.value.data) || {}
        if (responseData.error) {
          this.scorecard = null
          this.drivers = []
        } else {
          this.scorecard = responseData.fleet_average || null
          this.drivers = responseData.drivers || []
          this.thresholds = responseData.scorecard_thresholds || null
        }
      } catch (e) {
        this.scorecard = null
        this.drivers = []
      }
      this.loading = false
    },
  },
}
</script>
